<template>
  <div class="layout">
    <aside class="sidebar">
      <div class="brand">
        <span class="brand-icon">F</span>
        <div class="brand-text">
          <div class="logo">FAQ 知识库</div>
          <div class="logo-sub">后台管理中心</div>
        </div>
      </div>
      <div class="role-tag">{{ roleText }}</div>
      <nav class="nav">
        <div v-for="group in menuGroups" :key="group.key" class="nav-group">
          <button
            type="button"
            class="nav-group-head"
            :class="{ open: isGroupOpen(group.key) }"
            @click="toggleGroup(group.key)"
          >
            <span class="nav-icon">{{ group.icon }}</span>
            <span class="nav-label">{{ group.label }}</span>
            <span class="nav-arrow">{{ isGroupOpen(group.key) ? '▾' : '▸' }}</span>
          </button>
          <div v-show="isGroupOpen(group.key)" class="nav-sub">
            <button
              v-for="item in group.children"
              :key="item.path"
              type="button"
              :class="{ active: isActive(item.path) }"
              @click="$router.push(item.path)"
            >
              <span class="nav-icon">{{ item.icon }}</span>
              <span class="nav-label">{{ item.label }}</span>
            </button>
          </div>
        </div>
      </nav>
      <div class="foot">
        <button @click="logout">退出登录</button>
      </div>
    </aside>

    <div class="workspace">
      <header class="topbar">
        <div class="topbar-main">
          <span class="page-icon">📋</span>
          <div class="topbar-titles">
            <!-- <span class="breadcrumb">FAQ 后台</span> -->
            <h1 class="page-title">{{ pageTitle }}</h1>
          </div>
        </div>
      </header>
      <main class="content" :class="{ 'content-fill': contentFill }">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { authApi } from '@/api'
import { getMenuGroups } from '@/config/menu'

export default {
  name: 'AdminLayout',
  data() {
    return {
      user: {},
      expandedGroups: {}
    }
  },
  computed: {
    roleText() {
      const name = this.user.roleName || '用户'
      return `${name}${this.user.acc ? ' · ' + this.user.acc : ''}`
    },
    menuGroups() {
      return getMenuGroups(this.user.role || '')
    },
    pageTitle() {
      return (this.$route.meta && this.$route.meta.title) || ''
    },
    contentFill() {
      return this.$route.name === 'ExamQuestions'
    }
  },
  watch: {
    '$route.path'() {
      this.syncExpandedGroups()
    },
    menuGroups() {
      this.syncExpandedGroups()
    }
  },
  created() {
    authApi.getUser().then(res => {
      this.user = res.data || {}
    }).catch(() => {})
    this.syncExpandedGroups()
  },
  methods: {
    isGroupOpen(key) {
      return !!this.expandedGroups[key]
    },
    toggleGroup(key) {
      const opening = !this.isGroupOpen(key)
      const next = {}
      this.menuGroups.forEach(g => {
        next[g.key] = opening && g.key === key
      })
      this.expandedGroups = next
    },
    syncExpandedGroups() {
      const next = {}
      this.menuGroups.forEach(group => {
        next[group.key] = group.children.some(item => this.isActive(item.path))
      })
      this.expandedGroups = next
    },
    isActive(path) {
      if (path === '/knowledge') return this.$route.path.startsWith('/knowledge')
      // 在线答题 / 考试结果没有独立菜单，高亮回「我的考试」
      if (path === '/exam/my-exams') return /^\/exam\/(my-exams|take|complete|result)/.test(this.$route.path)
      if (path.startsWith('/exam/')) return this.$route.path === path
      return this.$route.path === path
    },
    async logout() {
      await authApi.logout()
      this.$router.replace('/login')
    }
  }
}
</script>

<style lang="scss" scoped>
.layout {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: $bg;
}

.sidebar {
  width: 220px;
  flex-shrink: 0;
  background: linear-gradient(180deg, $sidebar-bg 0%, darken($sidebar-bg, 3%) 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  border-right: 1px solid rgba(255, 255, 255, 0.06);

  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 16px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .brand-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, $lblue, $teal);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
  }

  .logo {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .logo-sub {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 2px;
  }

  .role-tag {
    padding: 10px 16px 6px;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    line-height: 1.4;
  }

  .nav {
    flex: 1;
    padding: 8px 10px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .nav-group {
    margin-bottom: 6px;
  }

  .nav-group-head,
  .nav-sub button {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    text-align: left;
    padding: 11px 12px;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.72);
    font-size: 13px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    .nav-icon {
      font-size: 15px;
      width: 20px;
      text-align: center;
      flex-shrink: 0;
    }

    .nav-label {
      flex: 1;
      min-width: 0;
    }

    .nav-badge {
      flex-shrink: 0;
      min-width: 18px;
      padding: 0 5px;
      border-radius: 9px;
      background: $amber;
      color: #fff;
      font-size: 11px;
      line-height: 18px;
      text-align: center;
    }

    &:hover {
      background: $sidebar-hover;
      color: #fff;
    }
  }

  .nav-group-head {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.88);

    &.open {
      color: #fff;
    }

    .nav-arrow {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.45);
      flex-shrink: 0;
    }
  }

  .nav-sub {
    padding: 2px 0 4px 8px;

    button {
      padding: 9px 12px 9px 16px;
      margin-bottom: 2px;
      font-size: 12px;
      font-weight: 400;
      color: rgba(255, 255, 255, 0.62);

      &.active {
        background: $sidebar-active;
        color: #fff;
        box-shadow: inset 3px 0 0 $lblue;
        border-radius: 0 8px 8px 0;
      }
    }
  }

  .foot {
    padding: 14px 16px 18px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);

    button {
      background: rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.85);
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 9px 12px;
      border-radius: 8px;
      font-size: 13px;
      width: 100%;
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: rgba(255, 255, 255, 0.14);
      }
    }
  }
}

.workspace {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100vh;
  overflow: hidden;
}

.topbar {
  flex-shrink: 0;
  height: 58px;
  background: $surface;
  border-bottom: 1px solid $line;
  box-shadow: $topbar-shadow;
  display: flex;
  align-items: center;
  padding: 0 28px;
  z-index: 10;

  .topbar-main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .page-icon {
    font-size: 20px;
    line-height: 1;
  }

  .topbar-titles {
    min-width: 0;
  }

  .breadcrumb {
    display: block;
    font-size: 11px;
    color: $grey;
    letter-spacing: 0.02em;
    margin-bottom: 2px;
  }

  .page-title {
    font-size: 17px;
    font-weight: 600;
    color: $navy;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 28px 32px;
  background:
    radial-gradient(ellipse at 20% 0%, rgba(74, 159, 212, 0.06) 0%, transparent 50%),
    $bg;

  &.content-fill {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;

    > :deep(*) {
      flex: 1;
      min-height: 0;
    }
  }
}
</style>
